import {noop} from '@github-ui/noop'
import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {memo, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import styles from './CopilotAnimation.module.css'
import Activate from './states/Activate'
import Sprite from './states/Sprite'
import {spriteRegistry} from './states/sprites'
import {CopilotAnimationState, CopilotAnimationType} from './types'

// eslint-disable-next-line no-barrel-files/no-barrel-files
export {CopilotAnimationType} from './types'

type AnimationTime = {
  [key: string]: number
}

const animationTimes: Record<string, AnimationTime> = {
  [CopilotAnimationType.Thinking]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Starting]: 429,
    [CopilotAnimationState.Running]: 1390,
    [CopilotAnimationState.Ending]: 429,
  },
  [CopilotAnimationType.Affirmative]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Running]: 627,
  },
  [CopilotAnimationType.Celebrate]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Running]: 1056,
  },
  [CopilotAnimationType.Negative]: {
    [CopilotAnimationState.Idle]: 250,
    [CopilotAnimationState.Running]: 792,
  },
  [CopilotAnimationType.Idle]: {
    [CopilotAnimationState.Running]: 13483,
  },
  [CopilotAnimationType.Confirm]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Running]: 627,
  },
  [CopilotAnimationType.UserInput]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Starting]: 363,
    [CopilotAnimationState.Running]: 3000,
    [CopilotAnimationState.Ending]: 528,
  },
  [CopilotAnimationType.Tickle]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Running]: 1188,
  },
  [CopilotAnimationType.JumpWiggle]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Running]: 1254,
  },
  [CopilotAnimationType.Activate]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Running]: 2000,
  },
}

export interface CopilotAnimationProps {
  /**
   * Select which animation to show. Note that this may not always update immediately - the component waits for any
   * running animation to finish before switching to the new one.
   */
  animationType: CopilotAnimationType
  /**
   * Control whether to loop the animation.
   * @default false
   */
  loopAnimation: boolean
  /**
   * Callback emitted when the animation ends. Is not called if `loopAnimation` is true.
   */
  onAnimationEnd?: () => void
  /**
   * The size of the animation. Cannot be smaller than 16.
   * Suggested sizes are 16, 24, 32, 48, 64, 96, 128.
   * @default 32
   */
  size?: number
  className?: string
  style?: React.CSSProperties
}

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const CopilotAnimation = memo(function CopilotAnimation({
  animationType,
  onAnimationEnd = noop,
  loopAnimation,
  className,
  style,
  size = 32,
}: CopilotAnimationProps) {
  const [animationState, setAnimationState] = useState<CopilotAnimationState>(CopilotAnimationState.Idle)
  const startTimeRef = useRef<number | null>(null)
  const computedSize = useMemo(() => (size >= 16 ? size : 16), [size])

  const scale = useMemo(() => (computedSize ? computedSize / 32 : 1), [computedSize])

  useEffect(() => {
    startTimeRef.current = null
    setAnimationState(CopilotAnimationState.Idle)
  }, [animationType])

  const onAnimationEndRef = useEffectEvent(onAnimationEnd)

  useEffect(() => {
    const animationTime = animationTimes[animationType]

    if (!animationTime || animationType === CopilotAnimationType.Static) return

    let timeoutId: NodeJS.Timeout
    if (animationState === CopilotAnimationState.Idle && (loopAnimation || startTimeRef.current === null)) {
      timeoutId = setTimeout(() => {
        setAnimationState(CopilotAnimationState.Starting)
      }, animationTime[CopilotAnimationState.Idle] || 0)
    }

    if (animationState === CopilotAnimationState.Starting) {
      timeoutId = setTimeout(() => {
        setAnimationState(CopilotAnimationState.Running)
        startTimeRef.current = Date.now()
      }, animationTime[CopilotAnimationState.Starting] || 0)
    }

    if (animationState === CopilotAnimationState.Running && !loopAnimation) {
      const elapsed = startTimeRef.current ? Date.now() - startTimeRef.current : 0
      const runningAnimationDuration = animationTime[CopilotAnimationState.Running] || 0
      const remainingTime = runningAnimationDuration - (elapsed % runningAnimationDuration)
      timeoutId = setTimeout(() => {
        setAnimationState(CopilotAnimationState.Ending)
      }, remainingTime)
    }

    if (animationState === CopilotAnimationState.Ending) {
      timeoutId = setTimeout(() => {
        setAnimationState(CopilotAnimationState.Idle)
        if (!loopAnimation) onAnimationEndRef()
      }, animationTime[CopilotAnimationState.Ending] || 0)
    }

    return () => clearTimeout(timeoutId)
  }, [animationState, loopAnimation, animationType])

  const copilotSVG = useMemo(() => {
    if (animationType === CopilotAnimationType.Activate) {
      return <Activate state={animationState} scale={scale} />
    }
    const spriteEntry = spriteRegistry[animationType]
    if (!spriteEntry) return null
    const {sprite, className: spriteClassName} = spriteEntry
    return <Sprite state={animationState} sprite={sprite} className={spriteClassName} />
  }, [animationType, animationState, scale])

  return (
    <div
      className={clsx(
        className,
        styles.copilotAnimationHolder,
        animationType === CopilotAnimationType.Activate && styles.activate,
      )}
      {...testIdProps('copilot-animation')}
      style={{...style, '--copilot-animation-scale': scale}}
    >
      <div
        className={clsx(styles.copilotAnimation, animationType === CopilotAnimationType.Activate && styles.activate)}
      >
        {copilotSVG}
      </div>
    </div>
  )
})
